// src/components/settings/PermissionManagement.js
import React, { useState } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  Snackbar,
} from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { Save } from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useFirestore } from "../../hooks/useFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";

// Mismos IDs que en RoleManagement
const MODULES = [
  { id: "contacts", label: "Contactos" },
  { id: "clients", label: "Clientes" },
  { id: "suppliers", label: "Proveedores" },
  { id: "commercials", label: "Comerciales" },
  { id: "companies", label: "Empresas" },
  { id: "projects", label: "Proyectos" },
  { id: "proposals", label: "Propuestas" },
  { id: "contracts", label: "Contratos" },
  { id: "sales", label: "Ventas (Facturas)" },
  { id: "purchases", label: "Compras (Facturas)" },
  { id: "calendar", label: "Calendario" },
  { id: "tasks", label: "Tareas" },
  { id: "settings", label: "Configuración" },
  { id: "chat", label: "Chat" },
  { id: "accounting", label: "Contabilidad" },
];

const PERMISSIONS = [
  { id: "view", label: "Ver" },
  { id: "create", label: "Crear" },
  { id: "edit", label: "Editar" },
  { id: "delete", label: "Eliminar" },
];

const PermissionManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { data: roles, loading: rolesLoading } = useCollection("roles");
  const firestore = useFirestore("roles");
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  React.useEffect(() => {
    if (roles && roles.length > 0) {
      console.log("📋 Cargando permisos para roles:", roles.map(r => ({ id: r.id, name: r.name })));
      const perms = {};
      roles.forEach((role) => {
        perms[role.id] = {};
        MODULES.forEach((module) => {
          if (role.modules?.[module.id]) {
            perms[role.id][module.id] = {
              view: role.modules[module.id].view !== false,
              create: role.modules[module.id].create !== false,
              edit: role.modules[module.id].edit !== false,
              delete: role.modules[module.id].delete !== false,
            };
          } else {
            perms[role.id][module.id] = {
              view: false,
              create: false,
              edit: false,
              delete: false,
            };
          }
        });
      });
      console.log("✅ Permisos cargados:", perms);
      setPermissions(perms);
    }
  }, [roles]);

  const handleToggle = (roleId, moduleId, permType) => {
    console.log(`🔄 Toggle: rol=${roleId}, modulo=${moduleId}, permiso=${permType}`);
    setPermissions((prev) => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        [moduleId]: {
          ...prev[roleId][moduleId],
          [permType]: !prev[roleId][moduleId]?.[permType],
        },
      },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      console.log("💾 Guardando permisos...");
      for (const roleId of Object.keys(permissions)) {
        const role = roles.find(r => r.id === roleId);
        if (role) {
          const updateData = {
            modules: permissions[roleId],
          };
          console.log(`  Actualizando rol ${roleId} (${role.name}):`, Object.keys(updateData.modules).filter(k => updateData.modules[k].view));
          await firestore.update(roleId, updateData);
        }
      }
      console.log("✅ Permisos guardados");
      setSnackbar({ open: true, message: "Permisos guardados correctamente", severity: "success" });
    } catch (error) {
      console.error("❌ Error:", error);
      setSnackbar({ open: true, message: "Error: " + error.message, severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  if (rolesLoading) return <LoadingSpinner />;

  if (!roles || roles.length === 0) {
    return (
      <EmptyState
        title="No hay roles configurados"
        description="Primero crea roles en la pestaña 'Roles' para poder asignar permisos"
        compact
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h6" fontWeight={600}>Matriz de Permisos</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              borderRadius: "12px", textTransform: "none", fontWeight: 600,
              height: 44, boxShadow: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" },
              "&:hover": { boxShadow: `0 4px 12px ${theme.palette.primary.main}40`, filter: "brightness(1.05)" },
            }}
          >
            {saving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </Stack>
      </Box>

      <Card elevation={2} sx={{ borderRadius: { xs: 1, sm: 2 }, overflow: "hidden", border: "1px solid", borderColor: theme.palette.divider, background: theme.palette.background.paper }}>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.08)", color: theme.palette.primary.main, position: "sticky", left: 0, zIndex: 2, minWidth: 180 }}>
                  Módulo / Permiso
                </TableCell>
                {roles.map((role) => (
                  <TableCell key={role.id} align="center" sx={{ fontWeight: 700, backgroundColor: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.08)", color: theme.palette.primary.main, minWidth: 120 }}>
                    <Chip label={role.name} size="small" sx={{ background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.secondary.main}20)`, color: theme.palette.primary.main, fontWeight: 600 }} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {MODULES.map((module) => (
                <React.Fragment key={module.id}>
                  <TableRow>
                    <TableCell colSpan={roles.length + 1} sx={{ backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0, 95, 101, 0.02)", fontWeight: 700, py: 1.5, color: theme.palette.secondary.main, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
                      {module.label}
                    </TableCell>
                  </TableRow>
                  {PERMISSIONS.map((perm) => (
                    <TableRow key={perm.id} hover>
                      <TableCell sx={{ position: "sticky", left: 0, backgroundColor: theme.palette.background.paper, zIndex: 1, pl: 4, fontSize: { xs: "0.8rem", sm: "0.85rem" } }}>
                        {perm.label}
                      </TableCell>
                      {roles.map((role) => (
                        <TableCell key={role.id} align="center" sx={{ backgroundColor: permissions[role.id]?.[module.id]?.[perm.id] ? `${theme.palette.success.main}08` : "transparent" }}>
                          <Switch
                            size="small"
                            checked={permissions[role.id]?.[module.id]?.[perm.id] || false}
                            onChange={() => handleToggle(role.id, module.id, perm.id)}
                            color="primary"
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <MuiAlert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ borderRadius: "12px", width: "100%" }}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default PermissionManagement;